/**
 * components/Card
 * ──────────────────────────────────────────────────────────────────────────
 * The container every screen is built from: 1px #E4E4E4 border, 8px radius
 * (10px for the elevated surfaces), white fill, and — critically — clipped
 * children, so a header's #FAFAFA fill and a footer's top rule meet the
 * rounded corner cleanly instead of squaring it off.
 *
 * A card is deliberately NOT a padded box. Most cards in this design have a
 * header strip, a body and sometimes a footer, each with its own padding and
 * its own fill; `padded` is the shortcut for the minority that are one block.
 * ──────────────────────────────────────────────────────────────────────────
 */
import type { ReactNode } from 'react';
import { StyleSheet, View, type ViewStyle } from 'react-native';
import { neutral } from '../../theme/colors';
import { radii } from '../../theme/radii';
import { card as cardSpace } from '../../theme/spacing';

export interface CardProps {
  children: ReactNode;
  /** Apply the standard 15px interior padding. */
  padded?: boolean;
  /** 18px instead of 15px — the wider padding used by KPI and balance cards. */
  roomy?: boolean;
  /** 10px radius: the modal shell and the drawer panel. */
  large?: boolean;
  /** A 3px accent strip along the top edge — KPI cards and the balance card. */
  accent?: string;
  style?: ViewStyle;
  testID?: string;
}

export function Card({
  children,
  padded = false,
  roomy = false,
  large = false,
  accent,
  style,
  testID,
}: CardProps) {
  return (
    <View
      testID={testID}
      style={[
        styles.card,
        { borderRadius: large ? radii.cardLg : radii.card },
        style,
      ]}
    >
      {!!accent && <View style={[styles.accent, { backgroundColor: accent }]} />}
      <View
        style={
          padded
            ? { padding: roomy ? cardSpace.paddingLg : cardSpace.padding }
            : undefined
        }
      >
        {children}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    borderWidth: 1,
    borderColor: neutral.border,
    backgroundColor: neutral.surface,
    // Clipping is what lets header fills and hairline grids reach the corner.
    overflow: 'hidden',
  },
  accent: { height: cardSpace.accentStripHeight, width: '100%' },
});

export default Card;
